import * as React from "react";
import { Link } from "react-router-dom";

interface LinkBaseProps {
  col?: "inherited" | string;
  style?: React.CSSProperties | null;
  [props: string]: any;
}

interface PersonLinkProps extends LinkBaseProps {
  identifier: string;
}

interface PersonEditorLinkProps extends LinkBaseProps {
  person?: string | null;
}

interface ShiftLinkProps extends LinkBaseProps {
  shiftId: string;
}

interface ShiftSessionLinkProps extends ShiftLinkProps {
  sessionId: string;
  openInNewTab?: boolean;
}

interface GameLinkProps extends LinkBaseProps {
  gameId: string;
}

interface CompetitionLinkProps extends LinkBaseProps {
  competitionId: string;
}

interface PublicLinkProps extends LinkBaseProps {
  token: string | null;
}

interface TeamLinkProps extends LinkBaseProps {
  teamId: string;
}

const HomeLink: React.FunctionComponent<LinkBaseProps> = ({
  col,
  style,
  children,
  ...other
}) => (
  <Link to="/" style={{ color: col || "inherit", ...style }} {...other}>
    {children || "Home"}
  </Link>
);

const PersonLink: React.FunctionComponent<PersonLinkProps> = ({
  identifier,
  col,
  children,
  style,
  ...other
}) => (
  <Link
    to={`/u/${identifier}`}
    style={{ color: col || "inherit", ...style }}
    {...other}
  >
    {children || identifier}
  </Link>
);

const PersonEditorLink: React.FunctionComponent<PersonEditorLinkProps> = ({
  col,
  person,
  children,
  style,
  ...other
}) => (
  <Link
    to={`/admin/persons/${person || ""}`}
    style={{ color: col || "inherit", ...style }}
    {...other}
  >
    {children}
  </Link>
);

const ShiftLink: React.FunctionComponent<ShiftLinkProps> = ({
  col,
  shiftId,
  children,
  style,
  ...other
}) => (
  <Link
    style={{ color: col || "inherit", ...style }}
    to={`/roster/${shiftId}`}
    {...other}
  >
    {children}
  </Link>
);

const getShiftSessionUrl = (shiftId: string, sessionId: string) =>
  `/roster/${shiftId}/${sessionId}`;
const getShiftManagerUrl = (shiftId: string) => `/admin/shifts/${shiftId}`;

const ShiftSessionLink: React.FunctionComponent<ShiftSessionLinkProps> = ({
  col,
  shiftId,
  sessionId,
  children,
  style,
  openInNewTab,
  ...other
}) => {
  const link = getShiftSessionUrl(shiftId, sessionId);
  return openInNewTab ? (
    <a
      href={link}
      target="_blank"
      style={{ color: col || "inherit", ...style }}
      {...other}
    >
      {children}
    </a>
  ) : (
    <Link to={link} style={{ color: col || "inherit", ...style }} {...other}>
      {children}
    </Link>
  );
};

const ShiftManagerLink: React.FunctionComponent<ShiftLinkProps> = ({
  col,
  shiftId,
  children,
  style,
  ...other
}) => (
  <Link
    style={{ color: col || "inherit", ...style }}
    to={getShiftManagerUrl(shiftId)}
    {...other}
  >
    {children}
  </Link>
);

const GameLink: React.FunctionComponent<GameLinkProps> = ({
  gameId,
  children,
  style,
  col,
  ...other
}) => (
  <Link
    to={`/g/${gameId}`}
    style={{ color: col || "inherit", ...style }}
    {...other}
  >
    {children || gameId}
  </Link>
);

const PrivacyLink: React.FunctionComponent<LinkBaseProps> = ({
  col,
  style,
  children,
  ...other
}) => (
  <Link style={{ color: col || "inherit", ...style }} to="/privacy" {...other}>
    {children || "Privacy policy"}
  </Link>
);

const CompetitionLink: React.FunctionComponent<CompetitionLinkProps> = ({
  competitionId,
  col,
  style,
  children,
  ...other
}) => (
  <Link
    style={{ color: col || "inherit", ...style }}
    to={`/competitions/${competitionId}`}
    {...other}
  >
    {children || "competition"}
  </Link>
);

const PublicRosterLink: React.FunctionComponent<PublicLinkProps> = ({
  token,
  col,
  style,
  children,
  ...other
}) => (
  <Link
    style={{ color: col || "inherit", ...style }}
    to={`/shift/published/${token}`}
    {...other}
  >
    {children || "see published roster"}
  </Link>
);

const PublicRosterAnchor: React.FunctionComponent<PublicLinkProps> = ({
  token,
  col,
  style,
  children,
  ...other
}) => (
  <a
    style={{ color: col || "inherit", ...style }}
    href={token ? `/shift/published/${token}` : undefined}
    target="_blank"
    {...other}
  >
    {children || "see published roster"}
  </a>
);

const TeamLink: React.FunctionComponent<TeamLinkProps> = ({
  teamId,
  col,
  style,
  children,
  ...other
}) => (
  <Link
    style={{ color: col || "inherit", ...style }}
    to={`/teams/${teamId}`}
    {...other}
  >
    {children || "team"}
  </Link>
);

export {
  CompetitionLink, GameLink, getShiftManagerUrl, getShiftSessionUrl, HomeLink, PersonEditorLink, PersonLink, PrivacyLink, PublicRosterAnchor, PublicRosterLink, ShiftLink, ShiftManagerLink, ShiftSessionLink, TeamLink
};

